import { Link } from 'react-router';

import { Badge, Card, CardHeader, StatusPill, cn, focusRing } from '@/design-system';
import {
    MEMBERSHIP_STATUS_LABELS,
    MEMBERSHIP_STATUS_TONES,
    type PlatformUser,
} from '@/entities/user';
import { ROLE_LABELS } from '@/shared/api';
import { formatDayLong, formatDayShort, formatDayTimeShort } from '@/shared/lib';
import { CardTitleRow } from '@/shared/ui';

import { paths } from '@/app/router/paths';
import { adminCopy } from '../copy';

export interface UserDetailProps {
    user: PlatformUser;
}

/**
 * Eén persoon over alle organisaties heen: het account, en bij welke bedrijven
 * hij werkt in welke rol. Vanuit een actief lidmaatschap kun je direct
 * meekijken - bij dát bedrijf, want je impersoneert een lidmaatschap en niet
 * een persoon.
 */
export function UserDetail({ user }: UserDetailProps) {
    const facts: Array<{ label: string; value: string }> = [
        { label: adminCopy.tenantEmail, value: user.email },
        { label: adminCopy.userPhone, value: user.phone ?? adminCopy.unknown },
        {
            label: adminCopy.userLastLogin,
            value: user.lastLoginAt
                ? formatDayTimeShort(user.lastLoginAt)
                : adminCopy.userNeverLoggedIn,
        },
        {
            label: adminCopy.userSince,
            value: user.createdAt ? formatDayLong(user.createdAt) : adminCopy.unknown,
        },
    ];

    return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-[14px]">
            <Card clip as="section" aria-labelledby="user-account-title">
                <CardHeader
                    title={<span id="user-account-title">{adminCopy.userContactTitle}</span>}
                    action={
                        user.isPlatformAdmin ? (
                            <Badge variant="text" tone="accent">
                                {adminCopy.userPlatformAdmin}
                            </Badge>
                        ) : undefined
                    }
                />
                <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3 px-4 py-[14px]">
                    {facts.map((fact) => (
                        <div key={fact.label} className="min-w-0">
                            <dt className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                                {fact.label}
                            </dt>
                            <dd className="m-0 mt-[2px] truncate text-[13px] text-[var(--text-primary)]">
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </Card>

            <Card clip as="section" aria-labelledby="user-memberships-title">
                <CardTitleRow
                    title={
                        <span id="user-memberships-title">
                            {adminCopy.userMembershipsTitle(user.name)}
                        </span>
                    }
                />

                {user.memberships.length === 0 ? (
                    <p className="m-0 px-4 py-[13px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {adminCopy.userMembershipsEmpty}
                    </p>
                ) : (
                    <ul
                        className="m-0 list-none p-0"
                        aria-label={adminCopy.userMembershipsLabel(user.name)}
                    >
                        {user.memberships.map((membership) => (
                            <li
                                key={membership.tenantUuid}
                                className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[var(--border-subtle-2)] px-4 py-[10px]"
                            >
                                <Link
                                    to={paths.adminTenant(membership.tenantUuid)}
                                    className={cn(
                                        'min-w-[140px] flex-1 truncate rounded-[2px] text-[13px] font-medium text-[var(--text-primary)] underline-offset-2 hover:text-[var(--accent)] hover:underline',
                                        focusRing,
                                    )}
                                >
                                    {membership.tenantName}
                                </Link>
                                <span className="text-[12px] text-[var(--text-secondary)]">
                                    {membership.role
                                        ? ROLE_LABELS[membership.role]
                                        : adminCopy.unknownRole}
                                </span>
                                <StatusPill
                                    tone={MEMBERSHIP_STATUS_TONES[membership.status]}
                                    size="sm"
                                >
                                    {MEMBERSHIP_STATUS_LABELS[membership.status]}
                                </StatusPill>
                                <span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-tertiary-safe)]">
                                    {membership.joinedAt
                                        ? adminCopy.memberSince(formatDayShort(membership.joinedAt))
                                        : adminCopy.unknown}
                                </span>
                                {membership.status === 'active' && !user.isPlatformAdmin ? (
                                    <Link
                                        to={`${paths.adminTenant(membership.tenantUuid)}?gebruiker=${encodeURIComponent(user.uuid)}#meekijken`}
                                        className={cn(
                                            'rounded-[2px] text-[12px] text-[var(--accent)] underline-offset-2 hover:text-[var(--accent-dark)] hover:underline',
                                            focusRing,
                                        )}
                                    >
                                        {adminCopy.userImpersonateAt(membership.tenantName)}
                                    </Link>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
